import { useCallback } from 'react';
import { usePlayheadStore } from '../store/playheadStore';
import { useEditorStore } from '../store/editorStore';
import { useLoopStore } from '../store/loopStore';
import { getAudioEngine } from '../renderer/rendererBridge';
import { frameToMs } from '../lib/timeUtils';

/** Map timeline time to source media time using clip sourceOffsetMs */
function resolveAudioSourceTime(timelineMs: number): { sourceMs: number; assetId?: string } {
  const tracks = useEditorStore.getState().project.tracks;
  for (const track of tracks) {
    if (track.type !== 'audio') continue;
    for (const clip of track.clips) {
      if (timelineMs >= clip.startMs && timelineMs < clip.startMs + clip.durationMs) {
        return {
          sourceMs: clip.sourceOffsetMs + (timelineMs - clip.startMs),
          assetId: clip.assetId,
        };
      }
    }
  }
  // No audio clip at this time
  return { sourceMs: -1 };
}

export function usePlayback() {
  const playing = usePlayheadStore((s) => s.playing);
  const currentTimeMs = usePlayheadStore((s) => s.currentTimeMs);
  const fps = useEditorStore((s) => s.project.fps);

  const play = useCallback(() => {
    // If there's a selection, start from selectionStart
    const loop = useLoopStore.getState();
    if (loop.selectionStart !== null && loop.selectionEnd !== null) {
      usePlayheadStore.getState().setCurrentTime(loop.selectionStart);
    }
    usePlayheadStore.getState().setPlaying(true);
    const audio = getAudioEngine();
    const { sourceMs, assetId } = resolveAudioSourceTime(usePlayheadStore.getState().currentTimeMs);
    if (sourceMs >= 0) {
      audio?.play(sourceMs, assetId);
    }
  }, []);

  const pause = useCallback(() => {
    usePlayheadStore.getState().setPlaying(false);
    getAudioEngine()?.stop();
  }, []);

  const togglePlayback = useCallback(() => {
    const s = usePlayheadStore.getState();
    if (s.playing) {
      usePlayheadStore.getState().setPlaying(false);
      getAudioEngine()?.stop();
    } else {
      // If there's a selection, start from selectionStart
      const loop = useLoopStore.getState();
      if (loop.selectionStart !== null && loop.selectionEnd !== null) {
        usePlayheadStore.getState().setCurrentTime(loop.selectionStart);
      }
      usePlayheadStore.getState().setPlaying(true);
      const audio = getAudioEngine();
      const { sourceMs, assetId } = resolveAudioSourceTime(usePlayheadStore.getState().currentTimeMs);
      if (sourceMs >= 0) {
        audio?.play(sourceMs, assetId);
      }
    }
  }, []);

  const seekTo = useCallback((ms: number) => {
    usePlayheadStore.getState().setCurrentTime(ms);
    const audio = getAudioEngine();
    if (audio?.playing) {
      const { sourceMs, assetId } = resolveAudioSourceTime(ms);
      if (sourceMs >= 0) {
        audio.play(sourceMs, assetId);
      } else {
        audio.stop();
      }
    }
  }, []);

  const stepForward = useCallback(() => {
    const s = usePlayheadStore.getState();
    s.setCurrentTime(s.currentTimeMs + frameToMs(1, fps));
  }, [fps]);

  const stepBackward = useCallback(() => {
    const s = usePlayheadStore.getState();
    s.setCurrentTime(s.currentTimeMs - frameToMs(1, fps));
  }, [fps]);

  const goToStart = useCallback(() => {
    usePlayheadStore.getState().setCurrentTime(0);
    const audio = getAudioEngine();
    if (audio?.playing) {
      const { sourceMs, assetId } = resolveAudioSourceTime(0);
      if (sourceMs >= 0) {
        audio.play(sourceMs, assetId);
      } else {
        audio.stop();
      }
    }
  }, []);

  const goToEnd = useCallback(() => {
    const duration = useEditorStore.getState().project.durationMs;
    usePlayheadStore.getState().setCurrentTime(duration);
    getAudioEngine()?.stop();
    usePlayheadStore.getState().setPlaying(false);
  }, []);

  return {
    playing,
    currentTimeMs,
    play,
    pause,
    togglePlayback,
    seekTo,
    stepForward,
    stepBackward,
    goToStart,
    goToEnd,
  };
}
